import { useNavigate } from 'react-router';
import { useCreatePhoto, useUploadImageToS3 } from '../../../service/requests';
import {
  type Response,
  isFailure,
  isSuccess,
  loading,
} from '../../../service/response';
import { toUrl, createRoute } from '../../../util/routes';
import { type UnescoSite, type Location } from '../../../util/types';
import { v4 as uuid } from 'uuid';
import imageCompression from 'browser-image-compression';

interface UploadPhotoProps {
  file: File;
  siteMetadata: UnescoSite | Location;
}

export const useUploadPhoto = ({
  file,
  siteMetadata,
}: UploadPhotoProps): any => {
  const navigate = useNavigate();
  const { uploadImageToS3 } = useUploadImageToS3();

  const uploadPhoto = async (): Promise<Response<any, any>> => {
    const photoId = uuid();

    const compressedThumbnailImage = await imageCompression(file, {
      maxWidthOrHeight: 400,
      fileType: 'image/webp',
      useWebWorker: true,
    });
    const compressedGalleryImage = await imageCompression(file, {
      maxWidthOrHeight: 1200,
      fileType: 'image/webp',
      useWebWorker: true,
    });

    const compressedMainImage = await imageCompression(file, {
      maxWidthOrHeight: 2000,
      fileType: 'image/webp',
      useWebWorker: true,
    });

    const uploadThumbnailResp = await uploadImageToS3(
      `${photoId}/thumbnail`,
      compressedThumbnailImage,
    );
    const uploadGalleryResp = await uploadImageToS3(
      `${photoId}/gallery`,
      compressedGalleryImage,
    );
    const uploadMainResp = await uploadImageToS3(
      `${photoId}/main`,
      compressedMainImage,
    );

    if (
      isSuccess(uploadThumbnailResp) &&
      isSuccess(uploadGalleryResp) &&
      isSuccess(uploadMainResp)
    ) {
      const { createPhoto } = useCreatePhoto({
        ...siteMetadata,
        ...(siteMetadata.siteName && {
          siteName: encodeURI(siteMetadata.siteName),
        }),
        ...(siteMetadata.subSiteName && {
          subSiteName: encodeURI(siteMetadata.subSiteName),
        }),
        ...(siteMetadata.locationName && {
          locationName: encodeURI(siteMetadata.locationName),
        }),
        photoId,
      });

      const resp = await createPhoto();

      if (!isFailure(resp) && !isSuccess(resp)) {
        return loading();
      }

      if (isSuccess(resp)) {
        navigate(toUrl(createRoute('photography', {})));
      }

      return resp;
    }

    if (
      isFailure(uploadThumbnailResp) ||
      isFailure(uploadGalleryResp) ||
      isFailure(uploadMainResp)
    ) {
      return uploadThumbnailResp;
    }
  };
  return {
    uploadPhoto,
  };
};
